import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import { FrequencyUnits, IssueMachineHealthResponse } from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import { addHours, subHours } from 'date-fns';
import { getIndicatorFromAlert } from 'pages/Location/alerts_and_drafts/assets_with_alerts/alert_item/utils';
import {
    getChartRangeFromTimeRange,
    getSessionCreateRequestData,
    isSelectedPointValid,
    isWaterfallSelectedPointValid,
    loadWorkbenchStateFromBrowserStorage,
    maxHarmonicMarkers,
} from 'pages/workbench/shared';
import { DefaultIndicatorSelections } from 'pages/workbench/side_panel/shared';
import {
    _WorkbenchStateInternal,
    WorkbenchState,
    WorkbenchStateValues,
} from 'pages/workbench/state';
import { MarkerModes, TimeMarkerMeta } from 'pages/workbench/types';
import { selectedSeriesPointAreEqual } from 'pages/workbench/workbench_tabs/trend/shared';
import { TimeMarkerId } from 'pages/workbench/workbench_tabs/trend/types';
import { isValueInChartRange } from 'shared/chart/shared/data';
import { ZustandContextInitializer } from 'shared/zustand_context/zustand_context';
import {
    addMissingItems,
    excludeNullAndUndefined,
    lastItem,
    removeAllItems,
    skipLast,
    toggleArrayItem,
    unique,
} from 'utils/array';
import { getNextTrendLineColor, getTrendLineColorForIndex } from 'utils/colors';
import { buildOrderBy } from 'utils/query_v3';

const AlertTimeRangeHours = 24;

const DefaultChartRangeStateValues: Pick<
    WorkbenchStateValues,
    | 'zoomRange'
    | 'fixedTimeSeriesLeftYAxisRange'
    | 'timeSeriesLeftYAxisZoom'
    | 'fixedTimeSeriesRightYAxisRange'
    | 'timeSeriesRightYAxisZoom'
    | 'frequencyChartZoomRange'
    | 'fixedFrequencyChartYAxisRange'
    | 'frequencyChartYAxisZoom'
    | 'frequencyChartScale'
    | 'timeChartZoomRange'
    | 'fixedTimeChartYAxisRange'
    | 'timeChartYAxisZoom'
    | 'waterfallChartScale'
    | 'waterfallChartZoomRange'
    | 'fixedWaterfallChartYAxisRange'
    | 'waterfallChartYAxisZoom'
    | 'waterfallChartSeriesOffset'
> = {
    zoomRange: undefined,
    fixedTimeSeriesLeftYAxisRange: undefined,
    timeSeriesLeftYAxisZoom: undefined,
    fixedTimeSeriesRightYAxisRange: undefined,
    timeSeriesRightYAxisZoom: undefined,
    frequencyChartZoomRange: undefined,
    fixedFrequencyChartYAxisRange: undefined,
    frequencyChartYAxisZoom: undefined,
    frequencyChartScale: 'linear',
    timeChartZoomRange: undefined,
    fixedTimeChartYAxisRange: undefined,
    timeChartYAxisZoom: undefined,
    waterfallChartScale: 'linear',
    waterfallChartZoomRange: undefined,
    fixedWaterfallChartYAxisRange: undefined,
    waterfallChartYAxisZoom: undefined,
    waterfallChartSeriesOffset: undefined,
};

export const DefaultIndicatorFilterStateValues: Pick<
    WorkbenchStateValues,
    | 'customIndicatorFilterIds'
    | 'indicatorTypes'
    | 'sensorRoleTypes'
    | 'filtered'
    | 'orientations'
    | 'dataTypes'
    | 'calculations'
    | 'bands'
    | 'labels'
> = {
    customIndicatorFilterIds: [],
    indicatorTypes: [],
    sensorRoleTypes: [],
    filtered: undefined,
    orientations: [],
    dataTypes: [],
    calculations: [],
    bands: [],
    labels: [],
};

export const DefaultFrequencyWaterfallStateValues: Pick<
    WorkbenchStateValues,
    'waterfallSelectedPoint' | 'waterfallFrequencyPoints' | 'waterfallChartSeriesOffset'
> = {
    waterfallSelectedPoint: undefined,
    waterfallFrequencyPoints: [],
    waterfallChartSeriesOffset: undefined,
};

export const DefaultHealthRangeStateValues: Pick<
    WorkbenchStateValues,
    'isAddingHealthRange' | 'selectedHealthRangeIds' | 'editingHealthRangeId'
> = {
    isAddingHealthRange: false,
    selectedHealthRangeIds: [],
    editingHealthRangeId: undefined,
};

const timeMarkerIds: TimeMarkerId[] = ['ruler', '1x', 'bpfo', 'bpfi', 'bsf', 'ftf'];

export const DefaultTimeMarkerMeta: TimeMarkerMeta[] = timeMarkerIds.map((id) => ({
    id,
    visible: false,
    zeroCycleMs: 0,
}));

export const DefaultWorkbenchStateValues: WorkbenchStateValues = {
    ...DefaultChartRangeStateValues,
    ...DefaultIndicatorFilterStateValues,
    ...DefaultFrequencyWaterfallStateValues,
    ...DefaultHealthRangeStateValues,
    runningSpeedLocked: true,
    isInitialized: false,
    runningSpeedHz: 0,
    runningSpeedUnit: FrequencyUnits.Hertz,
    frequencyBurstDataType: 'acceleration',
    timeBurstDataType: 'acceleration',
    waterfallBurstDataType: 'acceleration',
    sidebandFrequencyHz: 0,
    sidebandCount: 3,
    bearingId: undefined,
    rulerMarkerFrequencyHz: 0,
    sidebandMarkerId: undefined,
    harmonicMarkerIds: [],
    selectedMarkerIds: [],
    timeMarkerMeta: DefaultTimeMarkerMeta,
    showActivityLine: true,
    isTimeSeriesChartExpanded: true,
    showFrequencySpectrumChart: true,
    isFrequencySpectrumChartExpanded: true,
    showTimeDomainChart: true,
    isTimeDomainChartExpanded: true,
    showTimeMarkerTable: true,
    showFrequencyMarkerTable: true,
    showWaterfallValuesTable: true,
    isWaterfallChartExpanded: false,
    selectedPoint: undefined,
    frequencyDomainSelectedPoint: undefined,
    timeDomainSelectedPoint: undefined,
    showMinMax: false,
    showThresholds: false,
    autoScroll: false,
    splineSmoothing: false,
    waterfallSplineSmoothing: false,
    showBaseline: false,
    isTreeVisible: true,
    expandedGroupIds: [],
    showTreeStatusIcons: false,
    showTreeOfflineIcons: false,
    showTreeIssueIcons: false,
    selectedGroupIds: [],
    selectedIndicators: [],
    indicatorSelectionMode: 'auto',
    isLoadingIndicators: true,
    frequencyChartScale: 'linear',
    waterfallChartScale: 'linear',
    indicatorFilterTabType: 'type',
    activityFilters: {
        categoryFilterIds: [],
        alertFilterIds: [],
        automaticFilterIds: [],
        machineIQFilterIds: [],
    },
    timeRange: undefined,
    range: undefined,
    markerMode: 'Vibration',
    pendingAcknowledgeAsset: undefined,
    downsamplingMethod: DataPlatformApi.DownsamplingMethod.Median,
};

export const getWorkbenchStateInitializer =
    (
        overrides: Partial<WorkbenchStateValues> = {},
    ): ZustandContextInitializer<WorkbenchState> =>
    (set, get) => {
        const workbenchState: _WorkbenchStateInternal = {
            ...DefaultWorkbenchStateValues,
            ...overrides,
            toggleRunningSpeedLocked: () =>
                set((state) => ({
                    runningSpeedLocked: !state.runningSpeedLocked,
                })),
            setRunningSpeedHz: (runningSpeedHz) =>
                set(({ runningSpeedUnit }) => ({
                    runningSpeedHz,
                    runningSpeedUnit:
                        runningSpeedHz === 0 && runningSpeedUnit === FrequencyUnits.Order
                            ? FrequencyUnits.Hertz
                            : runningSpeedUnit,
                })),
            setRunningSpeedUnit: (runningSpeedUnit) => set({ runningSpeedUnit }),
            setFrequencyBurstDataType: (frequencyBurstDataType) =>
                set({ frequencyBurstDataType, frequencyChartYAxisZoom: undefined }),
            setTimeBurstDataType: (timeBurstDataType) =>
                set({ timeBurstDataType, timeChartYAxisZoom: undefined }),
            setWaterfallBurstDataType: (waterfallBurstDataType) =>
                set({ waterfallBurstDataType, waterfallChartYAxisZoom: undefined }),
            setSidebandFrequencyHz: (sidebandFrequencyHz) => set({ sidebandFrequencyHz }),
            setSidebandCount: (sidebandCount) => set({ sidebandCount }),
            setBearing: (bearingId) => set({ bearingId }),
            clearBearing: () => set({ bearingId: undefined }),
            setRulerMarkerFrequencyHz: (rulerMarkerFrequencyHz) =>
                set({ rulerMarkerFrequencyHz }),
            toggleMarkerVisibility: (markerId) =>
                set(({ selectedMarkerIds, sidebandMarkerId, harmonicMarkerIds }) => ({
                    selectedMarkerIds: toggleArrayItem(selectedMarkerIds, markerId),
                    sidebandMarkerId:
                        selectedMarkerIds.includes(markerId) &&
                        markerId === sidebandMarkerId
                            ? undefined
                            : sidebandMarkerId,
                    harmonicMarkerIds:
                        selectedMarkerIds.includes(markerId) &&
                        harmonicMarkerIds.includes(markerId)
                            ? toggleArrayItem(harmonicMarkerIds, markerId)
                            : harmonicMarkerIds,
                })),
            toggleMarkerSidebandVisibility: (markerId) =>
                set(({ sidebandMarkerId, selectedMarkerIds }) => ({
                    sidebandMarkerId:
                        sidebandMarkerId === markerId ? undefined : markerId,
                    selectedMarkerIds: unique([...selectedMarkerIds, markerId]),
                })),
            toggleMarkerHarmonicVisibility: (markerId) =>
                set(({ harmonicMarkerIds, selectedMarkerIds }) => ({
                    harmonicMarkerIds:
                        harmonicMarkerIds.includes(markerId) ||
                        harmonicMarkerIds.length < maxHarmonicMarkers
                            ? toggleArrayItem(harmonicMarkerIds, markerId)
                            : harmonicMarkerIds,
                    selectedMarkerIds: unique([...selectedMarkerIds, markerId]),
                })),
            setTimeMarkerZeroCycleMs: (markerId, zeroCycleMs) =>
                set(({ timeMarkerMeta }) => ({
                    timeMarkerMeta: timeMarkerMeta.map((meta) =>
                        meta.id === markerId ? { ...meta, zeroCycleMs } : meta,
                    ),
                })),
            toggleTimeMarkerVisibility: (markerId) =>
                set(({ timeMarkerMeta }) => ({
                    timeMarkerMeta: timeMarkerMeta.map((meta) =>
                        meta.id === markerId ? { ...meta, visible: !meta.visible } : meta,
                    ),
                })),
            hideAllTimeMarkers: () =>
                set(({ timeMarkerMeta }) => ({
                    timeMarkerMeta: timeMarkerMeta.map((meta) => ({
                        ...meta,
                        visible: false,
                    })),
                })),
            toggleActivityLineVisibility: () =>
                set(({ showActivityLine }) => ({ showActivityLine: !showActivityLine })),
            toggleTimeSeriesChartExpansion: () =>
                set(({ isTimeSeriesChartExpanded: showTimeSeriesChart }) => ({
                    isTimeSeriesChartExpanded: !showTimeSeriesChart,
                })),
            toggleFrequencySpectrumChartVisibility: () =>
                set(({ showFrequencySpectrumChart }) => ({
                    showFrequencySpectrumChart: !showFrequencySpectrumChart,
                    isFrequencySpectrumChartExpanded: !showFrequencySpectrumChart,
                    showFrequencyMarkerTable: !showFrequencySpectrumChart,
                })),
            toggleFrequencySpectrumChartExpansion: () =>
                set(({ isFrequencySpectrumChartExpanded }) => ({
                    isFrequencySpectrumChartExpanded: !isFrequencySpectrumChartExpanded,
                })),
            toggleTimeDomainChartVisibility: () =>
                set(({ showTimeDomainChart }) => ({
                    showTimeDomainChart: !showTimeDomainChart,
                    isTimeDomainChartExpanded: !showTimeDomainChart,
                    showTimeMarkerTable: !showTimeDomainChart,
                })),
            toggleTimeDomainChartExpansion: () =>
                set(({ isTimeDomainChartExpanded }) => ({
                    isTimeDomainChartExpanded: !isTimeDomainChartExpanded,
                })),
            toggleWaterfallChartExpansion: () =>
                set(({ isWaterfallChartExpanded }) => ({
                    isWaterfallChartExpanded: !isWaterfallChartExpanded,
                })),
            toggleWaterfallValuesTableVisibility: () =>
                set(({ showWaterfallValuesTable }) => ({
                    showWaterfallValuesTable: !showWaterfallValuesTable,
                })),
            toggleFrequencyMarkerTableVisibility: () =>
                set(({ showFrequencyMarkerTable }) => ({
                    showFrequencyMarkerTable: !showFrequencyMarkerTable,
                })),
            toggleTimeMarkerTableVisibility: () =>
                set(({ showTimeMarkerTable }) => ({
                    showTimeMarkerTable: !showTimeMarkerTable,
                })),
            toggleAllTrendComponents: () =>
                set(
                    ({
                        showActivityLine,
                        showFrequencySpectrumChart,
                        showTimeDomainChart,
                    }) =>
                        showActivityLine ||
                        showFrequencySpectrumChart ||
                        showTimeDomainChart
                            ? {
                                  isTimeSeriesChartExpanded: true,
                                  showActivityLine: false,
                                  showFrequencySpectrumChart: false,
                                  showTimeDomainChart: false,
                              }
                            : {
                                  isTimeSeriesChartExpanded: true,
                                  showActivityLine: true,
                                  showFrequencySpectrumChart: true,
                                  isFrequencySpectrumChartExpanded: true,
                                  showTimeDomainChart: true,
                                  isTimeDomainChartExpanded: true,
                              },
                ),
            setSelectedPoint: (selectedPoint, resetWaterfall = true) =>
                set(
                    ({
                        waterfallSelectedPoint,
                        waterfallFrequencyPoints,
                        timeMarkerMeta,
                    }) => ({
                        selectedPoint,
                        frequencyDomainSelectedPoint: undefined,
                        timeDomainSelectedPoint: undefined,
                        waterfallSelectedPoint: resetWaterfall
                            ? undefined
                            : waterfallSelectedPoint,
                        waterfallFrequencyPoints: resetWaterfall
                            ? []
                            : waterfallFrequencyPoints,
                        // Reset the 0 cyc offset for time markers on point navigation to
                        // prevent them from going off the screen
                        timeMarkerMeta: timeMarkerMeta.map((meta) => ({
                            ...meta,
                            zeroCycleMs: 0,
                        })),
                    }),
                ),
            setTimeDomainSelectedPoint: (timeDomainSelectedPoint) =>
                set({ timeDomainSelectedPoint }),
            setFrequencySelectedPoint: (frequencyDomainSelectedPoint) =>
                set({ frequencyDomainSelectedPoint }),
            setWaterfallSelectedPoint: (waterfallSelectedPoint) =>
                set({ waterfallSelectedPoint }),
            toggleWaterfallPoint: (point) =>
                set(({ waterfallFrequencyPoints, isWaterfallChartExpanded }) => {
                    const newPoints = waterfallFrequencyPoints.some((waterfallPoint) =>
                        selectedSeriesPointAreEqual(waterfallPoint, point),
                    )
                        ? waterfallFrequencyPoints.filter(
                              (waterfallPoint) =>
                                  !selectedSeriesPointAreEqual(waterfallPoint, point),
                          )
                        : [...waterfallFrequencyPoints, point];

                    return {
                        waterfallFrequencyPoints: newPoints,
                        isWaterfallChartExpanded:
                            newPoints.length > 0 ? true : isWaterfallChartExpanded,
                    };
                }),
            setWaterfallPoints: (waterfallFrequencyPoints) =>
                set({ waterfallFrequencyPoints }),
            toggleMinMaxVisibility: () =>
                set(({ showMinMax }) => ({ showMinMax: !showMinMax })),
            toggleThresholdsVisibility: () =>
                set(({ showThresholds }) => ({
                    showThresholds: !showThresholds,
                })),
            toggleAutoScroll: () =>
                set(({ autoScroll }) => ({ autoScroll: !autoScroll })),
            toggleSplineSmoothing: () =>
                set(({ splineSmoothing }) => ({
                    splineSmoothing: !splineSmoothing,
                })),

            toggleWaterfallSplineSmoothing: () =>
                set(({ waterfallSplineSmoothing }) => ({
                    waterfallSplineSmoothing: !waterfallSplineSmoothing,
                })),
            toggleBaselineVisibility: () =>
                set(({ showBaseline }) => ({ showBaseline: !showBaseline })),
            setShowBaseline: (showBaseline) => set({ showBaseline }),
            hideAllMarkers: () =>
                set({
                    selectedMarkerIds: [],
                    sidebandMarkerId: undefined,
                    harmonicMarkerIds: [],
                }),
            toggleTreeVisibility: () =>
                set(({ isTreeVisible }) => ({ isTreeVisible: !isTreeVisible })),
            toggleTreeStatusIcons: () =>
                set(({ showTreeStatusIcons }) => ({
                    showTreeStatusIcons: !showTreeStatusIcons,
                })),
            toggleTreeOfflineIcons: () =>
                set(({ showTreeOfflineIcons }) => ({
                    showTreeOfflineIcons: !showTreeOfflineIcons,
                })),
            toggleTreeIssueIcons: () =>
                set(({ showTreeIssueIcons }) => ({
                    showTreeIssueIcons: !showTreeIssueIcons,
                })),
            toggleGroupExpanded: (treeItem) => {
                set(({ expandedGroupIds }) => ({
                    expandedGroupIds: toggleArrayItem(expandedGroupIds, treeItem.Id),
                }));
            },
            addGroupToExpanded: (groupId) => {
                set(({ expandedGroupIds }) => ({
                    expandedGroupIds: addMissingItems(expandedGroupIds, [groupId]),
                }));
            },
            removeGroupFromExpanded: (groupId) => {
                set(({ expandedGroupIds }) => ({
                    expandedGroupIds: removeAllItems(expandedGroupIds, [groupId]),
                }));
            },
            getSessionData: (systemId) => getSessionCreateRequestData(systemId, get()),
            updateFromSession: (
                {
                    TimeRange: SessionTimeRange,
                    SelectedPoint,
                    GroupIds,
                    IndicatorTypes,
                    MeasureOfs,
                    CustomFilterIds,
                    SelectedIndicatorIds,
                    IsCustomFilterType,
                },
                treeItems,
            ) =>
                set({
                    ...DefaultChartRangeStateValues,
                    ...DefaultFrequencyWaterfallStateValues,
                    ...DefaultHealthRangeStateValues,
                    timeRange: {
                        id: 'session',
                        type: 'custom',
                        startTimestamp: SessionTimeRange.StartDate.valueOf(),
                        endTimestamp: SessionTimeRange.EndDate.valueOf(),
                    },
                    range: {
                        min: SessionTimeRange.StartDate.valueOf(),
                        max: SessionTimeRange.EndDate.valueOf(),
                    },
                    zoomRange: {
                        min: SessionTimeRange.StartX.valueOf(),
                        max: SessionTimeRange.EndX.valueOf(),
                    },
                    frequencyChartScale: 'linear',
                    waterfallChartScale: 'linear',
                    selectedGroupIds: GroupIds,
                    expandedGroupIds: unique(
                        treeItems.flatMap((treeItem) =>
                            treeItem.Hierarchy.map((hierarchy) => hierarchy.HierarchyId),
                        ),
                    ),
                    selectedIndicators: SelectedIndicatorIds.map(
                        (indicatorId, index) => ({
                            indicatorId,
                            color: getTrendLineColorForIndex(index),
                        }),
                    ),
                    indicatorSelectionMode: 'manual',
                    selectedPoint:
                        SelectedPoint?.XValue !== undefined &&
                        SelectedPoint.SeriesId !== undefined
                            ? {
                                  xValue: SelectedPoint.XValue,
                                  seriesId: SelectedPoint.SeriesId,
                              }
                            : undefined,
                    customIndicatorFilterIds: CustomFilterIds,
                    sensorRoleTypes: MeasureOfs.map((measureOf) => measureOf.MeasureOf),
                    indicatorTypes: IndicatorTypes.map((type) => type.IndicatorType),
                    indicatorFilterTabType: IsCustomFilterType ? 'custom' : 'type',
                }),
            updateFromIndicator: (indicator) => {
                const groupIds = indicator.HierarchyInfo.hierarchy.map(
                    ({ HierarchyId }) => HierarchyId,
                );
                const lastGroupId = lastItem(groupIds);

                if (lastGroupId) {
                    set({
                        ...DefaultChartRangeStateValues,
                        ...DefaultIndicatorFilterStateValues,
                        ...DefaultFrequencyWaterfallStateValues,
                        ...DefaultHealthRangeStateValues,
                        selectedGroupIds: [lastGroupId],
                        expandedGroupIds: groupIds,
                        selectedIndicators: [
                            {
                                indicatorId: indicator.Id,
                                color: getTrendLineColorForIndex(0),
                            },
                        ],
                        indicatorSelectionMode: 'manual',
                    });
                }
            },
            updateFromAlert: (alert) => {
                const groupIds = alert.Hierarchy.map(({ HierarchyId }) => HierarchyId);
                const lastGroupId = lastItem(groupIds);
                const halfHours = AlertTimeRangeHours / 2;
                const startTimestamp = subHours(alert.TimeOfAlert, halfHours).getTime();
                const endTimestamp = addHours(alert.TimeOfAlert, halfHours).getTime();
                const indicator = getIndicatorFromAlert(alert);

                if (lastGroupId) {
                    set({
                        ...DefaultChartRangeStateValues,
                        ...DefaultIndicatorFilterStateValues,
                        ...DefaultFrequencyWaterfallStateValues,
                        ...DefaultHealthRangeStateValues,
                        selectedGroupIds: [lastGroupId],
                        expandedGroupIds: groupIds,
                        selectedIndicators: [
                            {
                                indicatorId: indicator.id,
                                color: getTrendLineColorForIndex(0),
                            },
                        ],
                        timeRange: {
                            id: 'alert',
                            type: 'custom',
                            startTimestamp,
                            endTimestamp,
                        },
                        zoomRange: undefined,
                        selectedPoint: undefined,
                        range: { min: startTimestamp, max: endTimestamp },
                        indicatorSelectionMode: 'manual',
                    });
                }
            },
            updateFromActivityEvent: (activityEvent, groups, indicators) => {
                const { selectedGroupIds, expandedGroupIds } =
                    indicators.length > 0
                        ? {
                              selectedGroupIds: indicators.map(
                                  (indicator) =>
                                      lastItem(indicator.Hierarchy)?.HierarchyId,
                              ),
                              expandedGroupIds: skipLast(
                                  indicators.flatMap((indicator) =>
                                      indicator.Hierarchy.map(
                                          (hierarchyChild) => hierarchyChild.HierarchyId,
                                      ),
                                  ),
                                  1,
                              ),
                          }
                        : {
                              selectedGroupIds: groups.map((group) => group.Id),
                              expandedGroupIds: groups.flatMap((group) =>
                                  group.Hierarchy.map(
                                      (hierarchyChild) => hierarchyChild.HierarchyId,
                                  ),
                              ),
                          };
                const selectedIndicators = indicators.map((indicator, index) => ({
                    indicatorId: indicator.Id,
                    color: getTrendLineColorForIndex(index),
                }));
                const hasIndicators = selectedIndicators.length > 0;

                const halfHours = AlertTimeRangeHours / 2;

                const startTime = activityEvent.StartDateTime.getTime();
                const endTime = activityEvent.EndDateTime?.getTime();

                const isDifferentEndTime = endTime !== undefined && endTime !== startTime;

                const startTimestamp = isDifferentEndTime
                    ? startTime
                    : subHours(startTime, halfHours).getTime();
                const endTimestamp = isDifferentEndTime
                    ? endTime
                    : addHours(startTime, halfHours).getTime();

                set({
                    ...DefaultChartRangeStateValues,
                    ...DefaultIndicatorFilterStateValues,
                    ...DefaultFrequencyWaterfallStateValues,
                    ...DefaultHealthRangeStateValues,
                    selectedGroupIds: excludeNullAndUndefined(unique(selectedGroupIds)),
                    expandedGroupIds: unique(expandedGroupIds),
                    selectedIndicators,
                    timeRange: {
                        id: 'activity-event',
                        type: 'custom',
                        startTimestamp,
                        endTimestamp,
                    },
                    zoomRange: undefined,
                    selectedPoint: undefined,
                    range: { min: startTimestamp, max: endTimestamp },
                    indicatorSelectionMode: hasIndicators ? 'manual' : 'auto',
                    isLoadingIndicators: !hasIndicators,
                });
            },
            updateFromTreeItemId: async (treeItemId) => {
                const treeItem = await sdClient.getTreeItem({ treeItemId });

                set({
                    ...DefaultChartRangeStateValues,
                    ...DefaultIndicatorFilterStateValues,
                    ...DefaultFrequencyWaterfallStateValues,
                    ...DefaultHealthRangeStateValues,
                    selectedGroupIds: [treeItem.Id],
                    expandedGroupIds: treeItem.Hierarchy.map(
                        (hierarchy) => hierarchy.HierarchyId,
                    ),
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            updateFromTreeItem: (treeItem) => {
                set({
                    ...DefaultChartRangeStateValues,
                    ...DefaultIndicatorFilterStateValues,
                    ...DefaultFrequencyWaterfallStateValues,
                    ...DefaultHealthRangeStateValues,
                    selectedGroupIds: [treeItem.Id],
                    expandedGroupIds: treeItem.Hierarchy.map(
                        (hierarchy) => hierarchy.HierarchyId,
                    ),
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            updateFromIssue: async (issue) => {
                const indicators =
                    issue instanceof IssueMachineHealthResponse && issue.Group?.GroupId
                        ? await sdClient
                              .getIndicators({
                                  groupIds: [issue.Group.GroupId],
                                  pageLimit: DefaultIndicatorSelections,
                                  orderBy: buildOrderBy([
                                      { field: 'SystemSortOrder', dir: 'asc' },
                                      { field: 'Id', dir: 'asc' },
                                  ]),
                              })
                              .then((res) =>
                                  res.Result.map((indicator, index) => ({
                                      indicatorId: indicator.Id,
                                      color: getTrendLineColorForIndex(index),
                                  })),
                              )
                        : [];

                const selectedGroupIds = issue.Group?.GroupId
                    ? [issue.Group.GroupId]
                    : [];
                const expandedGroupIds = issue.Hierarchy.map((item) => item.HierarchyId);

                set({
                    ...DefaultChartRangeStateValues,
                    ...DefaultIndicatorFilterStateValues,
                    ...DefaultFrequencyWaterfallStateValues,
                    ...DefaultHealthRangeStateValues,
                    selectedGroupIds,
                    expandedGroupIds,
                    selectedIndicators: indicators,
                    indicatorSelectionMode: 'manual',
                    isLoadingIndicators: false,
                });
            },
            updateFromChartData: async (
                issue,
                {
                    indicators,
                    timeRange,
                    selectedPoint,
                    frequencyBurstDataType,
                    timeBurstDataType,
                    waterfallBurstDataType,
                    waterfallFrequencyPoints,
                },
            ) => {
                // It's possible for chart indicators to fail to load and thus be undefined. We naturally don't
                // want to select these, so filter them out.
                const chartIndicators = indicators
                    .filter(
                        ({ indicatorId, indicator }) =>
                            indicatorId !== undefined && indicator !== undefined,
                    )
                    .map(({ indicatorId, color }) => ({
                        // We're filtering out undefined indicator ids above, this is just to satisfy the type
                        indicatorId: indicatorId ?? '',
                        color,
                    }));

                // If there are no chart indicators, load and default to the issue group indicators.
                const issueIndicators =
                    issue instanceof IssueMachineHealthResponse &&
                    issue.Group?.GroupId &&
                    chartIndicators.length === 0
                        ? await sdClient
                              .getIndicators({
                                  groupIds: [issue.Group.GroupId],
                                  pageLimit: DefaultIndicatorSelections,
                                  orderBy: buildOrderBy([
                                      { field: 'SystemSortOrder', dir: 'asc' },
                                      { field: 'Id', dir: 'asc' },
                                  ]),
                              })
                              .then((res) =>
                                  res.Result.map((indicator, index) => ({
                                      indicatorId: indicator.Id,
                                      color: getTrendLineColorForIndex(index),
                                  })),
                              )
                        : [];
                const selectedIndicators =
                    chartIndicators.length > 0 ? chartIndicators : issueIndicators;

                const selectedGroupIds = issue.Group?.GroupId
                    ? [issue.Group.GroupId]
                    : [];
                const expandedGroupIds = issue.Hierarchy.map((item) => item.HierarchyId);
                const range = getChartRangeFromTimeRange(timeRange);

                set({
                    ...DefaultChartRangeStateValues,
                    ...DefaultIndicatorFilterStateValues,
                    ...DefaultFrequencyWaterfallStateValues,
                    ...DefaultHealthRangeStateValues,
                    selectedIndicators,
                    indicatorSelectionMode: 'manual',
                    isLoadingIndicators: false,
                    selectedGroupIds,
                    expandedGroupIds,
                    timeRange,
                    range,
                    selectedPoint,
                    frequencyBurstDataType:
                        frequencyBurstDataType ??
                        DefaultWorkbenchStateValues.frequencyBurstDataType,
                    timeBurstDataType:
                        timeBurstDataType ??
                        DefaultWorkbenchStateValues.timeBurstDataType,
                    waterfallBurstDataType:
                        waterfallBurstDataType ??
                        DefaultWorkbenchStateValues.waterfallBurstDataType,
                    waterfallFrequencyPoints:
                        waterfallFrequencyPoints ??
                        DefaultFrequencyWaterfallStateValues.waterfallFrequencyPoints,
                    isWaterfallChartExpanded: waterfallFrequencyPoints !== undefined,
                });
            },
            updateFromBrowserStorage: (userEmail, systemId) => {
                const state = loadWorkbenchStateFromBrowserStorage(userEmail);
                const { timeRange } = get();

                if (state) {
                    const { systemId: storedSystemId, ...storedState } = state;

                    set(() =>
                        storedSystemId !== systemId
                            ? {
                                  ...DefaultIndicatorFilterStateValues,
                                  ...storedState,
                                  ...DefaultChartRangeStateValues,
                                  ...DefaultFrequencyWaterfallStateValues,
                                  selectedGroupIds: [systemId],
                                  expandedGroupIds: [],
                                  selectedIndicators: [],
                                  selectedPoint: undefined,
                                  indicatorSelectionMode: 'auto',
                                  isLoadingIndicators: true,
                              }
                            : storedState,
                    );
                    if (timeRange) {
                        set({
                            range: getChartRangeFromTimeRange(timeRange),
                            zoomRange: undefined,
                        });
                    }
                } else {
                    set(() => ({
                        ...DefaultChartRangeStateValues,
                        ...DefaultIndicatorFilterStateValues,
                        ...DefaultFrequencyWaterfallStateValues,
                        selectedGroupIds: [systemId],
                        expandedGroupIds: [],
                        indicatorSelectionMode: 'auto',
                        isLoadingIndicators: true,
                    }));
                    if (timeRange) {
                        set({
                            range: getChartRangeFromTimeRange(timeRange),
                            zoomRange: undefined,
                        });
                    }
                }
            },
            updateAutoScroll: () => {
                const { timeRange } = get();
                if (timeRange) {
                    set({
                        range: getChartRangeFromTimeRange(timeRange),
                        zoomRange: undefined,
                    });
                }
            },
            setActivityFilters: (activityFilters) => set({ activityFilters }),
            setTimeRange: (timeRange) =>
                set(({ autoScroll, selectedPoint, waterfallFrequencyPoints }) => {
                    const range = getChartRangeFromTimeRange(timeRange);
                    const selectedPointInTimeRange =
                        selectedPoint !== undefined &&
                        isValueInChartRange(selectedPoint.xValue, range);

                    return {
                        timeRange,
                        range,
                        zoomRange: undefined,
                        autoScroll: timeRange.type === 'custom' ? false : autoScroll,
                        selectedPoint: selectedPointInTimeRange
                            ? selectedPoint
                            : undefined,
                        waterfallFrequencyPoints: selectedPointInTimeRange
                            ? waterfallFrequencyPoints.filter((point) =>
                                  isValueInChartRange(point.xValue, range),
                              )
                            : [],
                    };
                }),
            setZoomRange: (zoomDateRange) =>
                set({
                    zoomRange: zoomDateRange,
                }),
            setFixedTimeSeriesLeftYAxisRange: (range) =>
                set({
                    fixedTimeSeriesLeftYAxisRange: range === 'auto' ? undefined : range,
                    timeSeriesLeftYAxisZoom: undefined,
                }),
            setTimeSeriesLeftYAxisZoom: (zoom) =>
                set({ timeSeriesLeftYAxisZoom: zoom === 'auto' ? undefined : zoom }),
            setFixedTimeSeriesRightYAxisRange: (range) =>
                set({
                    fixedTimeSeriesRightYAxisRange: range === 'auto' ? undefined : range,
                    timeSeriesRightYAxisZoom: undefined,
                }),
            setTimeSeriesRightYAxisZoom: (zoom) =>
                set({ timeSeriesRightYAxisZoom: zoom === 'auto' ? undefined : zoom }),
            setFrequencyChartZoomRange: (frequencyChartZoomRange) =>
                set({ frequencyChartZoomRange }),
            setFixedFrequencyChartYAxisRange: (range) =>
                set({
                    fixedFrequencyChartYAxisRange: range === 'auto' ? undefined : range,
                    frequencyChartYAxisZoom: undefined,
                }),
            setFrequencyChartYAxisZoom: (zoom) =>
                set({ frequencyChartYAxisZoom: zoom === 'auto' ? undefined : zoom }),
            setFrequencyChartScale: (frequencyChartScale) => set({ frequencyChartScale }),
            setTimeChartZoomRange: (timeChartZoomRange) => set({ timeChartZoomRange }),
            setFixedTimeChartYAxisRange: (range) =>
                set({
                    fixedTimeChartYAxisRange: range === 'auto' ? undefined : range,
                    timeChartYAxisZoom: undefined,
                }),
            setTimeChartYAxisZoom: (zoom) =>
                set({ timeChartYAxisZoom: zoom === 'auto' ? undefined : zoom }),
            setWaterfallChartZoomRange: (waterfallChartZoomRange) =>
                set({ waterfallChartZoomRange }),
            setFixedWaterfallChartYAxisRange: (range) =>
                set({
                    fixedWaterfallChartYAxisRange: range === 'auto' ? undefined : range,
                    waterfallChartYAxisZoom: undefined,
                }),
            setWaterfallChartYAxisZoom: (zoom) =>
                set({ waterfallChartYAxisZoom: zoom === 'auto' ? undefined : zoom }),
            setWaterfallChartScale: (waterfallChartScale) => set({ waterfallChartScale }),
            setWaterfallChartSeriesOffset: (waterfallChartSeriesOffset) =>
                set({ waterfallChartSeriesOffset }),
            resetTimeSeriesZoom: () =>
                set({
                    zoomRange: undefined,
                    timeSeriesLeftYAxisZoom: undefined,
                    timeSeriesRightYAxisZoom: undefined,
                }),
            resetFrequencyChartZoom: () => {
                set({
                    frequencyChartZoomRange: undefined,
                    frequencyChartYAxisZoom: undefined,
                });
            },
            resetTimeChartZoom: () => {
                set({
                    timeChartZoomRange: undefined,
                    timeChartYAxisZoom: undefined,
                });
            },
            resetWaterfallChartZoom: () => {
                set({
                    waterfallChartZoomRange: undefined,
                    waterfallChartYAxisZoom: undefined,
                });
            },
            setIndicatorFilterTabType: (indicatorFilterTabType) => {
                set({ indicatorFilterTabType });
            },
            clearIndicatorFilters: () => {
                set({
                    ...DefaultIndicatorFilterStateValues,
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            clearAdvancedFilters: () => {
                set({
                    dataTypes: [],
                    calculations: [],
                    bands: [],
                    labels: [],
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            setCustomIndicatorFilterIds: (customIndicatorFilterIds) => {
                set({
                    customIndicatorFilterIds,
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            setIndicatorTypes: (indicatorTypes) => {
                set({
                    indicatorTypes,
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            setSensorRoleTypes: (sensorRoleTypes) => {
                set({
                    sensorRoleTypes,
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            setFiltered: (filtered) => {
                set({
                    filtered,
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            setOrientations: (orientations) => {
                set({
                    orientations,
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            setDataTypes: (dataTypes) => {
                set({
                    dataTypes,
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            setCalculations: (calculations) => {
                set({
                    calculations,
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            setBands: (bands) => {
                set({
                    bands,
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            setLabels: (labels) => {
                set({
                    labels,
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                });
            },
            setSelectedGroup: (groupId) =>
                set({
                    selectedGroupIds: [groupId],
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                    timeChartZoomRange: undefined,
                }),
            toggleGroupSelected: (treeItem) =>
                set(({ selectedGroupIds }) => ({
                    selectedGroupIds: toggleArrayItem(selectedGroupIds, treeItem.Id),
                    indicatorSelectionMode: 'auto',
                    isLoadingIndicators: true,
                })),
            setSelectedIndicators: (selectedIndicators) =>
                set(
                    ({
                        selectedPoint,
                        waterfallSelectedPoint,
                        waterfallFrequencyPoints,
                    }) => {
                        const selectedPointValid = isSelectedPointValid(
                            selectedIndicators,
                            selectedPoint,
                        );
                        const waterfallPointValid =
                            selectedPointValid &&
                            isWaterfallSelectedPointValid(
                                selectedIndicators,
                                waterfallSelectedPoint,
                            );
                        const newWaterfallPoints = selectedPointValid
                            ? waterfallFrequencyPoints.filter((point) =>
                                  isSelectedPointValid(selectedIndicators, point),
                              )
                            : [];

                        return {
                            selectedIndicators,
                            selectedPoint: selectedPointValid ? selectedPoint : undefined,
                            waterfallSelectedPoint: waterfallPointValid
                                ? waterfallSelectedPoint
                                : undefined,
                            waterfallFrequencyPoints: newWaterfallPoints,
                            indicatorSelectionMode: 'manual',
                            timeSeriesLeftYAxisZoom: undefined,
                            timeSeriesRightYAxisZoom: undefined,
                        };
                    },
                ),
            toggleIndicator: (indicatorId) =>
                set(
                    ({
                        selectedIndicators,
                        selectedPoint,
                        waterfallSelectedPoint,
                        waterfallFrequencyPoints,
                    }) => {
                        const newSelectedIndicators = selectedIndicators.some(
                            (selectedIndicator) =>
                                selectedIndicator.indicatorId === indicatorId,
                        )
                            ? selectedIndicators.filter(
                                  (selectedIndicator) =>
                                      selectedIndicator.indicatorId !== indicatorId,
                              )
                            : [
                                  ...selectedIndicators,
                                  {
                                      indicatorId,
                                      color: getNextTrendLineColor(
                                          selectedIndicators.map(
                                              (selection) => selection.color,
                                          ),
                                      ),
                                  },
                              ];

                        const selectedPointValid = isSelectedPointValid(
                            newSelectedIndicators,
                            selectedPoint,
                        );
                        const waterfallPointValid =
                            selectedPointValid &&
                            isWaterfallSelectedPointValid(
                                newSelectedIndicators,
                                waterfallSelectedPoint,
                            );
                        const newWaterfallPoints = selectedPointValid
                            ? waterfallFrequencyPoints.filter((point) =>
                                  isSelectedPointValid(newSelectedIndicators, point),
                              )
                            : [];

                        return {
                            selectedIndicators: newSelectedIndicators,
                            selectedPoint: selectedPointValid ? selectedPoint : undefined,
                            waterfallSelectedPoint: waterfallPointValid
                                ? waterfallSelectedPoint
                                : undefined,
                            waterfallFrequencyPoints: newWaterfallPoints,
                            indicatorSelectionMode: 'manual',
                            timeSeriesLeftYAxisZoom: undefined,
                            timeSeriesRightYAxisZoom: undefined,
                        };
                    },
                ),
            setIsLoadingIndicators: (isLoadingIndicators) => set({ isLoadingIndicators }),
            setIsAddingHealthRange: (isAddingHealthRange) => set({ isAddingHealthRange }),
            setSelectedHealthRangeIds: (selectedHealthRangeIds) =>
                set({ selectedHealthRangeIds }),
            setEditingHealthRange: (healthRange, groupId) =>
                set(
                    ({
                        selectedIndicators,
                        selectedPoint,
                        waterfallSelectedPoint,
                        waterfallFrequencyPoints,
                    }) => {
                        const newSelectedIndicators =
                            healthRange.IndicatorIds?.map(
                                (id) =>
                                    selectedIndicators.find(
                                        (info) => info.indicatorId === id,
                                    ) ?? {
                                        indicatorId: id,
                                        color: getNextTrendLineColor(
                                            selectedIndicators.map((info) => info.color),
                                        ),
                                    },
                            ) ?? [];

                        const selectedPointValid = isSelectedPointValid(
                            newSelectedIndicators,
                            selectedPoint,
                        );

                        return {
                            editingHealthRangeId: healthRange.Id,
                            selectedGroupIds: [groupId],
                            selectedIndicators: newSelectedIndicators,
                            selectedPoint: selectedPointValid ? selectedPoint : undefined,
                            waterfallSelectedPoint: selectedPointValid
                                ? waterfallSelectedPoint
                                : undefined,
                            waterfallFrequencyPoints: selectedPointValid
                                ? waterfallFrequencyPoints
                                : [],
                            indicatorSelectionMode: 'manual',
                            timeSeriesLeftYAxisZoom: undefined,
                            timeSeriesRightYAxisZoom: undefined,
                        };
                    },
                ),
            stopEditingHealthRange: () => set({ editingHealthRangeId: undefined }),
            setPendingAcknowledgeAsset: (pendingAcknowledgeAsset) =>
                set({ pendingAcknowledgeAsset }),
            setIsInitialized: (isInitialized) => set({ isInitialized }),
            cycleMarkerMode: () =>
                set((state) => {
                    const nextIndex = MarkerModes.indexOf(state.markerMode) + 1;
                    return {
                        markerMode:
                            nextIndex >= MarkerModes.length
                                ? MarkerModes[0]
                                : MarkerModes[nextIndex],
                    };
                }),
            setMarkerMode: (markerMode) => set({ markerMode }),
            setDownsamplingMethod: (downsamplingMethod) => set({ downsamplingMethod }),
        };

        return workbenchState;
    };
